import { initializeApp } from "firebase/app";
import {
  Bytes,
  collection,
  CollectionReference,
  doc,
  getDoc,
  getDocs,
  initializeFirestore,
  persistentLocalCache,
  persistentSingleTabManager,
  query,
  setDoc,
  Timestamp,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import type {
  ChunkSerialized,
  DbChunk,
  DbGameSave,
  DbPlayer,
  SerializedGameState,
} from "~/state/game-types";
import type { ChunkMap } from "~/terrain/voxel-terrain";

const firebaseConfig = {
  apiKey: "AIzaSyCyJuI8PdpEm0cg3H6ymY-OYsjoOHBry1U",
  authDomain: "wamps-db.firebaseapp.com",
  databaseURL: "https://wamps-db.firebaseio.com",
  projectId: "wamps-db",
  appId: "1:562350150896:web:dc9e8f898061cb932ede35",
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const db = initializeFirestore(
  app,
  {
    localCache: persistentLocalCache({
      tabManager: persistentSingleTabManager({}),
      cacheSizeBytes: 1024 * 1024 * 200, // 200MB
    }),
  },
  "mining-game",
);

export const nowTimestamp = () => Timestamp.now();

const COLLECTIONS = {
  users: collection(db, "users") as CollectionReference<DbPlayer, DbPlayer>,
  gameSaves: (userId: string) =>
    collection(db, "users", userId, "game_saves") as CollectionReference<
      DbGameSave,
      DbGameSave
    >,
  chunks: (userId: string, gameSaveId: string) =>
    collection(
      db,
      "users",
      userId,
      "game_saves",
      gameSaveId,
      "chunks",
    ) as CollectionReference<DbChunk, DbChunk>,
} as const;

export function genFirestoreId(): string {
  return doc(collection(db, "a")).id;
}

export const firestoreService = new (class FirestoreService {
  async loadGameSaves(userId: string) {
    const gameSaves = await getDocs(
      query(COLLECTIONS.gameSaves(userId), where("user_id", "==", userId)),
    );

    return gameSaves.docs.map((doc) => doc.data());
  }

  /**
   * Save game state to Firestore
   */
  async saveGame(
    userId: string,
    gameSaveId: string | null,
    gameData: SerializedGameState,
    chunkMap: ChunkMap | null,
  ): Promise<{ id: string }> {
    if (!userId) {
      throw new Error("Cannot save game: No player ID");
    }

    const now = Timestamp.now();

    const chunk_count = chunkMap?.size ?? 0;

    if (!gameSaveId) {
      gameSaveId = genFirestoreId();
      const data: DbGameSave = {
        ...gameData,
        chunk_count,
        id: gameSaveId,
        user_id: userId,
        last_launched_at: null,
        created_at: now,
        last_saved_at: now,
      };
      await setDoc(doc(COLLECTIONS.gameSaves(userId), gameSaveId), data);
    } else {
      await updateDoc(doc(COLLECTIONS.gameSaves(userId), gameSaveId), {
        ...gameData,
        chunk_count,
        last_saved_at: now,
      });
    }

    let savedChunkCount = 0;
    if (chunkMap?.size) {
      const chunks = [];
      for (const chunk of chunkMap.values()) {
        if (!chunk.voxelStructs) continue;
        chunks.push({
          uid: chunk.id,
          x: chunk.chunkPlacement.x,
          y: chunk.chunkPlacement.y,
          terrain_types: chunk.voxelStructs.buffers.terrainType,
        });
      }
      await this.saveChunks(userId, gameSaveId, chunks);
      savedChunkCount = chunks.length;
    }

    console.log(`Game saved successfully, ${savedChunkCount} chunks saved`);

    return { id: gameSaveId };
  }

  async saveChunks(
    userId: string,
    gameSaveId: string,
    chunks: ChunkSerialized[],
  ): Promise<void> {
    if (!chunks.length) return;

    const batch = writeBatch(db);
    for (const chunk of chunks) {
      const chunkId = chunk.uid.toString();
      const chunkRef = doc(COLLECTIONS.chunks(userId, gameSaveId), chunkId);
      const data: DbChunk = {
        ...chunk,
        game_save_id: gameSaveId,
        terrain_types: Bytes.fromUint8Array(chunk.terrain_types),
      };
      batch.set(chunkRef, data);
    }
    await batch.commit();
  }

  async loadChunks(
    userId: string,
    gameSaveId: string,
    // chunkIds: string[],
  ): Promise<ChunkSerialized[]> {
    const data = await getDocs(
      query(
        COLLECTIONS.chunks(userId, gameSaveId),
        where("game_save_id", "==", gameSaveId),
      ),
    );
    const chunks = data.docs.map((doc) => {
      const data = doc.data();
      return {
        ...data,
        terrain_types: data.terrain_types.toUint8Array(),
      };
    });

    return chunks;
  }

  /**
   * Load game state from Firestore
   */
  async loadGameSave(
    userId: string,
    gameSaveId: string,
  ): Promise<DbGameSave | null> {
    const docSnap = await getDoc(
      doc(COLLECTIONS.gameSaves(userId), gameSaveId),
    );
    return docSnap.data() ?? null;
  }

  async getUser(userId: string): Promise<DbPlayer | null> {
    const docSnap = await getDoc(doc(COLLECTIONS.users, userId));
    return docSnap.data() ?? null;
  }

  async createUser(username: string): Promise<DbPlayer> {
    const userId = genFirestoreId();
    const data: DbPlayer = {
      id: userId,
      username,
      created_at: Timestamp.now(),
    };
    await setDoc(doc(COLLECTIONS.users, userId), data);
    return data;
  }

  async updateGameSave(
    userId: string,
    gameSaveId: string,
    data: Partial<DbGameSave>,
  ): Promise<void> {
    await updateDoc(doc(COLLECTIONS.gameSaves(userId), gameSaveId), data);
  }
})();
